import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type Entry = {
  id: number;
  channel: string;
  year: number;
  month: number;
  amount: number;
  note: string | null;
  updated_at: string | null;
};

type ListResp = {
  items: Entry[];
  year_totals: Record<string, number>;
  channels: string[];
};

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const fmtVND = (v: number) => v.toLocaleString("vi-VN") + " đ";

export function ManualRevenuePage() {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [channel, setChannel] = useState("TikTok");
  const [month, setMonth] = useState(1);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const q = useQuery({
    queryKey: ["manual-revenue", year],
    queryFn: async () =>
      (await api.get<ListResp>("/manual-revenue", { params: { year } })).data,
  });

  const upsert = useMutation({
    mutationFn: async (body: {
      channel: string;
      year: number;
      month: number;
      amount: number;
      note: string | null;
    }) => (await api.post("/manual-revenue", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manual-revenue"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setAmount("");
      setNote("");
    },
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/manual-revenue/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manual-revenue"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });

  // Pivot: rows = month, cols = channel
  const { channels, byMonth } = useMemo(() => {
    const items = q.data?.items ?? [];
    const channelSet = new Set<string>(q.data?.channels ?? []);
    items.forEach((it) => channelSet.add(it.channel));
    const channels = Array.from(channelSet).sort();
    const byMonth: Record<number, Record<string, Entry>> = {};
    items.forEach((it) => {
      if (!byMonth[it.month]) byMonth[it.month] = {};
      byMonth[it.month][it.channel] = it;
    });
    return { channels, byMonth };
  }, [q.data]);

  const channelTotals = useMemo(() => {
    const t: Record<string, number> = {};
    channels.forEach((c) => (t[c] = 0));
    (q.data?.items ?? []).forEach((it) => {
      t[it.channel] = (t[it.channel] || 0) + it.amount;
    });
    return t;
  }, [q.data, channels]);

  const monthTotal = (m: number): number => {
    const row = byMonth[m] || {};
    return Object.values(row).reduce((s, e) => s + e.amount, 0);
  };

  const yearGrandTotal = q.data?.year_totals?.[String(year)] ?? 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(amount.replace(/[^0-9]/g, ""));
    if (!channel.trim() || !amt) return;
    upsert.mutate({
      channel: channel.trim(),
      year,
      month,
      amount: amt,
      note: note.trim() || null,
    });
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">Doanh thu thủ công</h1>
      <p className="text-sm text-slate-500 mb-5">Nhập doanh thu theo tháng cho các kênh không sync tự động (TikTok, Shopee đã nghỉ...).</p>
      <div className="text-sm text-gray-500 mb-4">
        Nhập doanh thu các kênh ngoài POScake (TikTok, Shopee, ...) đã ngưng bán.
        Số liệu này KHÔNG kê khai vào Sổ S2a-HKD (báo cáo thuế), chỉ cộng vào tổng năm
        ở Tổng quan để theo dõi.
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">Năm:</label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border rounded px-2 py-1 text-sm"
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <div className="text-sm">
          Tổng năm {year}:{" "}
          <span className="font-semibold text-green-700">{fmtVND(yearGrandTotal)}</span>
        </div>
      </div>

      {/* Form nhập */}
      <form
        onSubmit={submit}
        className="bg-white border rounded p-3 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end"
      >
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Kênh</label>
          <input
            type="text"
            list="manual-rev-channels"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            placeholder="TikTok, Shopee..."
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <datalist id="manual-rev-channels">
            {channels.map((c) => (
              <option key={c} value={c} />
            ))}
            <option value="TikTok" />
            <option value="Shopee" />
            <option value="Lazada" />
            <option value="Facebook" />
          </datalist>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Tháng</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                Tháng {m}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-0.5">Số tiền (VND)</label>
          <input
            type="text"
            value={amount}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, "");
              setAmount(digits ? Number(digits).toLocaleString("vi-VN") : "");
            }}
            placeholder="60.425.914"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div className="md:col-span-2 flex gap-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ghi chú (tuỳ chọn)"
            className="flex-1 border rounded px-2 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={upsert.isPending || !channel.trim() || !amount}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
          >
            {upsert.isPending ? "Lưu..." : "Lưu"}
          </button>
        </div>
      </form>

      {upsert.error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
          {(upsert.error as any)?.response?.data?.detail ?? String(upsert.error)}
        </div>
      )}

      {/* Pivot table: rows = month, cols = channel */}
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Tháng</th>
              {channels.map((c) => (
                <th key={c} className="text-right px-3 py-2">
                  {c}
                </th>
              ))}
              <th className="text-right px-3 py-2 font-semibold">Tổng tháng</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((m) => {
              const row = byMonth[m] || {};
              const total = monthTotal(m);
              if (total === 0 && channels.every((c) => !row[c])) return null;
              return (
                <tr key={m} className="border-t">
                  <td className="px-3 py-1.5">Tháng {m}/{year}</td>
                  {channels.map((c) => {
                    const e = row[c];
                    if (!e) {
                      return (
                        <td key={c} className="px-3 py-1.5 text-right text-gray-300">
                          —
                        </td>
                      );
                    }
                    return (
                      <td
                        key={c}
                        className="px-3 py-1.5 text-right group cursor-pointer hover:bg-blue-50"
                        title={e.note || ""}
                        onClick={() => {
                          setChannel(e.channel);
                          setMonth(e.month);
                          setAmount(e.amount.toLocaleString("vi-VN"));
                          setNote(e.note || "");
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        <div className="inline-flex items-center gap-2">
                          <span className="font-mono">{fmtVND(e.amount)}</span>
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              if (confirm(`Xoá ${e.channel} tháng ${e.month}/${e.year}?`)) {
                                del.mutate(e.id);
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 text-red-500 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-right font-semibold text-green-700">
                    {fmtVND(total)}
                  </td>
                </tr>
              );
            })}
            {(q.data?.items.length ?? 0) === 0 && (
              <tr>
                <td
                  colSpan={channels.length + 2}
                  className="text-center text-gray-500 py-6"
                >
                  Chưa có dữ liệu cho năm {year}. Dùng form ở trên để nhập.
                </td>
              </tr>
            )}
          </tbody>
          {channels.length > 0 && (
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td className="px-3 py-2 font-semibold">Tổng năm {year}</td>
                {channels.map((c) => (
                  <td key={c} className="px-3 py-2 text-right font-semibold">
                    {fmtVND(channelTotals[c] || 0)}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold text-green-700">
                  {fmtVND(yearGrandTotal)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="text-xs text-gray-500 mt-3">
        Click vào ô bất kỳ để sửa. Hover để xoá.
      </div>
    </div>
  );
}
